'use client';
import { RefObject } from 'react';
import { Picture, Typography } from '@/app/common';
import { PlayButton } from '../ui/PlayButton';
import { Thumbnail as ThumbnailType } from '@workspace/interfaces';
import { cn } from '@workspace/lib';

interface VideoWithTextProps {
  wrapperRef: (node?: Element | null) => void;
  playerContainerRef: RefObject<HTMLDivElement | null>;
  thumbnail?: ThumbnailType;
  heading: string;
  description?: string;
  duration: string;
  isPlaying: boolean;
  isReady: boolean;
  onPlay: () => void;
  wrapperClassName?: string;
  aspectRatio?: string;
}

const fadeSlow = 'transition-opacity ease-in-out duration-[1200ms] md:duration-[1600ms] delay-[250ms] md:delay-[400ms]';

export function VideoWithText({
  wrapperRef,
  playerContainerRef,
  thumbnail,
  heading,
  description,
  duration,
  isPlaying,
  isReady,
  onPlay,
  wrapperClassName,
  aspectRatio,
}: VideoWithTextProps) {
  // Split background class
  const bgClasses =
    wrapperClassName
      ?.split(' ')
      .filter((cls) => cls.startsWith('bg-'))
      .join(' ') || '';
  const otherClasses =
    wrapperClassName
      ?.split(' ')
      .filter((cls) => !cls.startsWith('bg-'))
      .join(' ') || '';

  return (
    <div
      className={cn('relative w-full overflow-hidden', !isPlaying ? 'cursor-pointer' : 'cursor-auto', otherClasses)}
      style={aspectRatio ? { aspectRatio } : undefined}
      onClick={!isPlaying ? onPlay : undefined}
    >
      {/* Background layer */}
      <div className={cn('absolute inset-0', isPlaying ? 'bg-transparent' : '', bgClasses)}>
        {/* Video layer */}
        <div
          ref={playerContainerRef}
          className={cn(
            'absolute inset-0 w-full h-full overflow-hidden z-10',
            isPlaying && isReady ? 'opacity-100' : 'opacity-0',
          )}
        />

        {/* Thumbnail */}
        {thumbnail?.url && (
          <Picture
            fill
            image={thumbnail}
            alt={thumbnail.bynderImage?.alt || thumbnail.alt || 'Media Thumbnail'}
            desktopWidth={1920}
            desktopHeight={1080}
            mobileWidth={390}
            mobileHeight={219}
            imgClassName={cn(
              'absolute inset-0 w-full h-full object-cover z-10',
              isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100',
            )}
          />
        )}
      </div>

      {/* Content layer */}
      <div
        className={cn(
          'relative px-md py-sm md:p-4xl flex flex-col justify-end h-full gap-sm',
          isPlaying ? 'pointer-events-none' : '',
          'porta-container z-20',
        )}
        ref={wrapperRef}
      >
        {/* Info card overlay */}
        <div
          className={cn(
            'flex flex-col w-full gap-12 z-10',
            fadeSlow,
            isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col max-md:gap-xs bg-white/80 rounded-lg max-md:p-6 pl-sm p-3 md:p-lg md:w-149.75 md:gap-sm">
            <div className="flex flex-col gap-xs md:gap-sm">
              <Typography variant="bodySm" className="hidden md:block">
                {duration}
              </Typography>
              <Typography variant="h2" styleVariant="h3" className=" hidden md:block md:max-w-98">
                {heading}
              </Typography>
            </div>

            <div className="flex gap-sm md:gap-lg items-center">
              <PlayButton onPlay={onPlay} />
              <Typography variant="h3" className="md:max-w-98 block md:hidden">
                {heading}
              </Typography>
              <Typography variant="bodySm" className="md:max-w-94 hidden md:block">
                {description}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
